import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../axiosInstance';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import LoadingTable from '../../components/LoadingTable';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import getDecryptData from '../../../helpers/getDecryptData';
import OrderAdvancedFilters from '../../components/OrderAdvancedFilters';
import { useThemeContext } from '../../../fetchdata/ThemeContext';
import AssignDriverSelect, { DriverAvatar } from '../../components/AssignDriverSelect';
import BookingDetailsModal, { BookingDetailsButton } from '../../components/BookingDetailsModal';

const MySwal = withReactContent(Swal);

function PendingOrder() {
  const { sendMessage } = useThemeContext();
  const [AllDriver, setAllDriver] = useState([]);
  const [driverProfiles, setDriverProfiles] = useState({});
  const [DriverId, setDriverId] = useState(null);
  const [OrderId, setOrderId] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setlimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ status: '', comment: '' });
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelComment, setCancelComment] = useState('');
  const [cancelledBy, setCancelledBy] = useState('admin');
  const [filters, setFilters] = useState({ fromDate: '', toDate: '', minHours: '', maxHours: '', filterType: 'driver', sortBy: 'orderId' });
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedBookingOrder, setSelectedBookingOrder] = useState(null);

  /* ── Driver helpers ── */
  const handleDriverChange = (driverId) => {
    setDriverId(driverId || '');
  };

  const buildDriverProfileMap = (users = []) => (
    users.reduce((map, user) => {
      if (user?._id) map[user._id] = user;
      return map;
    }, {})
  );

  const mergeDriverProfile = (driver, profileMap = driverProfiles) => {
    if (!driver) return driver;
    const id = driver?._id || driver;
    const profileUser = profileMap[id];
    if (!profileUser || typeof driver !== 'object') return driver;
    return {
      ...driver,
      profile: driver.profile || profileUser.profile || profileUser.ProfileFile || '',
      ProfileFile: driver.ProfileFile || profileUser.ProfileFile || profileUser.profile || '',
      profileImage: driver.profileImage || profileUser.profileImage || '',
      avatar: driver.avatar || profileUser.avatar || '',
    };
  };

  const selectDriver = (ordId, driver, cat, type, distance, empType) => {
    fetchemployeeData(cat, type, empType, distance, ordId);
    setOrderId(ordId);
    setDriverId(driver);
  };

  const fetchemployeeData = async (cat, type, empType, distance, currentOrderId) => {
    setAllDriver([]);
    try {
      const [res, profileRes] = await Promise.all([
        axiosInstance.get(`/all-employee?type=${type}&longitude=${distance?.longitude || ''}&latitude=${distance?.latitude || ''}&empType=${empType}&status=1&currentOrderId=${currentOrderId || ''}`),
        axiosInstance.get('/admin/all-user?page=1&limit=1000&type=1&empType=2&status=1'),
      ]);
      const profileMap = buildDriverProfileMap(profileRes.data.users || []);
      setDriverProfiles((prev) => ({ ...prev, ...profileMap }));
      if (res.data.success) {
        setAllDriver((res.data.users || [])
          .filter((driver) => String(driver.status) !== '0')
          .map((driver) => mergeDriverProfile(driver, profileMap)));
      }
    } catch (e) { console.error('Error fetching Driver:', e); }
  };

  const handleClickDriverChange = async () => {
    try {
      const res = await axiosInstance.put(`/admin/update-employee-order/${OrderId}`, { agentId: DriverId });
      if (res.data.success) {
        const assignmentSocketPayload = {
          type: 'book',
          orderId: OrderId,
          userId: DriverId,
          order: {
            orderId: OrderId,
            status: 'assigned',
            driverId: DriverId,
          },
        };

        sendMessage(assignmentSocketPayload);

        try {
          await axiosInstance.post('/send-push-old', {
            title: 'New Ride Assigned!',
            body: 'You have been assigned a new ride. Tap to view details.',
            type: 1,
            department: 'driver',
            userId: DriverId,
            orderId: OrderId,
          });
        } catch (pushError) {
          console.error('Error sending assignment push notification', pushError);
        }

        toast.success('Employee Updated successfully!');
      }
    } catch (e) { console.error('Error Updated Employee', e); }
    finally { fetchData(); }
  };

  /* ── Data fetching ── */
  const fetchData = async (
    nextFilters = filters,
    nextDriver = selectedDriver,
    nextSearchTerm = searchTerm,
    nextPage = currentPage
  ) => {
    const dec = await getDecryptData();
    let employeeId = null;
    if (dec.type === 3) employeeId = dec._id;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: nextPage,
        limit,
        search: nextSearchTerm,
        status: '1,2,3,4,5,6',
        employeeId: employeeId || nextDriver?.value || '',
        filterType: employeeId ? 'driver' : nextFilters.filterType || 'driver',
        filterId: employeeId || nextDriver?.value || '',
        ...nextFilters,
      });
      const [res, profileRes] = await Promise.all([
        axiosInstance.get(`/admin/all-order?${params.toString()}`),
        axiosInstance.get('/admin/all-user?page=1&limit=1000&type=1&empType=2&status='),
      ]);
      const profileMap = buildDriverProfileMap(profileRes.data.users || []);
      setDriverProfiles((prev) => ({ ...prev, ...profileMap }));
      const orderList = (res.data.Order || []).map((order) => ({
        ...order,
        agentId: order.agentId ? mergeDriverProfile(order.agentId, profileMap) : order.agentId,
      }));
      setData(orderList);
      setCurrentPage(res.data.currentPage);
      setTotalPages(res.data.totalPages);
    } catch (e) {
      console.error(e);
      setData([]);
      setTotalPages(0);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [currentPage, limit]);

  const resetFilters = () => {
    const emptyFilters = { fromDate: '', toDate: '', minHours: '', maxHours: '', filterType: 'driver', sortBy: 'orderId' };
    setFilters(emptyFilters);
    setSelectedDriver(null);
    setSearchTerm('');
    setCurrentPage(1);
    fetchData(emptyFilters, null, '', 1);
  };

  /* ── Actions ── */
  const handleDelete = (id) => {
    MySwal.fire({ title: 'Are you sure?', text: 'You will not be able to recover this!', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, delete it!', reverseButtons: true })
      .then(r => { if (r.isConfirmed) axiosInstance.delete(`/admin/delete-order/${id}`).then(() => { fetchData(); toast.success('Ride deleted!'); }).catch(() => toast.error('Error deleting')); });
  };

  const handleLeadStatusChange = (id) => async (e) => {
    try {
      await axiosInstance.put(`/admin/update-order/${id}`, { status: e.target.value });
      fetchData(); toast.success('Status Updated!');
    } catch (err) { console.error(err); }
  };

  const handleCancelOrder = (id) => {
    if (!cancelComment) { toast.error('Comment is required'); return; }
    if (!cancelledBy) { toast.error('Cancel by is required'); return; }
    const cancelByLabels = { admin: 'admin', user: 'user', driver: 'driver' };
    MySwal.fire({ title: 'Cancel Ride?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, Cancel it!', reverseButtons: true })
      .then(r => {
        if (r.isConfirmed) {
          axiosInstance.put(`/cancel-order/${id}`, { comment: `Cancelled by ${cancelByLabels[cancelledBy] || 'admin'}: ${cancelComment}`, cancelledBy, skipCancelCharge: true })
            .then(() => { fetchData(); toast.success('Ride Cancelled!'); setCancelComment(''); setCancelledBy('admin'); setCancelOrderId(null); })
            .catch(() => toast.error('Error cancelling'));
        }
      });
  };

  const handleCompleteOrder = (id) => {
    MySwal.fire({ title: 'Complete Ride?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, Complete it!', reverseButtons: true })
      .then(r => {
        if (r.isConfirmed) {
          axiosInstance.put(`/complete-order/${id}`, { otp: 0 })
            .then(() => { fetchData(); toast.success('Ride Completed!'); })
            .catch((e) => toast.error(e?.response?.data?.message || 'Error completing'));
        }
      });
  };

  /* ── UI helpers ── */
  const getStatusConfig = (status) => {
    const map = {
      0: { label: 'Cancelled', color: '#ef4444', bg: '#fef2f2', icon: 'bi-x-circle-fill' },
      1: { label: 'Placed',    color: '#3b82f6', bg: '#eff6ff', icon: 'bi-clock-fill' },
      2: { label: 'Accepted',  color: '#8b5cf6', bg: '#f5f3ff', icon: 'bi-check-circle-fill' },
      3: { label: 'Scheduled', color: '#06b6d4', bg: '#ecfeff', icon: 'bi-calendar-check-fill' },
      4: { label: 'On the Way',color: '#f59e0b', bg: '#fffbeb', icon: 'bi-car-front-fill' },
      5: { label: 'Started',   color: '#f59e0b', bg: '#fffbeb', icon: 'bi-play-circle-fill' },
      6: { label: 'In Progress',color:'#0ea5e9', bg: '#f0f9ff', icon: 'bi-arrow-repeat' },
      7: { label: 'Completed', color: '#10b981', bg: '#ecfdf5', icon: 'bi-check2-all' },
    };
    return map[status] || { label: 'Unknown', color: '#6b7280', bg: '#f9fafb', icon: 'bi-question-circle' };
  };

  const getBookingType = (items) => {
    if (!items || !items.length) return 'N/A';
    const t = items[0]?.title || '';
    if (t.includes('Round')) return 'Round Trip';
    if (t.includes('One Way')) return 'One Way';
    if (t.includes('Outstation')) return 'Outstation';
    if (t.includes('Local')) return 'Local';
    return t;
  };

  const bookingColors = { 'Round Trip': { color: '#d97706', bg: '#fffbeb' }, 'One Way': { color: '#2563eb', bg: '#eff6ff' }, 'Outstation': { color: '#7c3aed', bg: '#f5f3ff' }, 'Local': { color: '#059669', bg: '#ecfdf5' }, 'N/A': { color: '#6b7280', bg: '#f9fafb' } };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="orders-page">

          <div className="page-header">
            <div className="page-title">
              <div className="title-icon pending-icon"><i className="bi bi-hourglass-split"></i></div>
              <div>
                <h1>Pending Rides</h1>
                <p>Active and in-progress bookings</p>
              </div>
            </div>
          </div>

          <div className="filter-card">
            <div className="filter-row order-filter-grid">
              <div className="filter-group filter-per-page">
                <label><i className="bi bi-list-ol me-1"></i>Per Page</label>
                <select value={limit} onChange={(e) => { setlimit(parseInt(e.target.value, 10)); setCurrentPage(1); }}>
                  {[5, 10, 15, 20, 25].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="filter-group filter-order-search">
                <label><i className="bi bi-search me-1"></i>Search by Ride ID</label>
                <div className="search-input-wrap">
                  <i className="bi bi-search search-icon"></i>
                  <input type="text" placeholder="Enter Ride ID..." value={searchTerm}
                    onChange={(e) => { setCurrentPage(1); setTotalPages(1); setSearchTerm(e.target.value); }}
                    onKeyDown={(e) => e.key === 'Enter' && fetchData()} />
                </div>
              </div>
              <OrderAdvancedFilters
                filters={filters}
                onFilterChange={(event) => setFilters((current) => ({
                  ...current,
                  [event.target.name]: event.target.value,
                }))}
                selectedDriver={selectedDriver}
                onDriverChange={(driver) => {
                  setSelectedDriver(driver);
                  setCurrentPage(1);
                }}
              />
              <div className="filter-group filter-sort">
                <label><i className="bi bi-sort-down me-1"></i>Sort By</label>
                <select
                  value={filters.sortBy}
                  onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}
                >
                  <option value="orderId">Order ID</option>
                  <option value="dateDesc">Date Newest</option>
                  <option value="dateAsc">Date Oldest</option>
                </select>
              </div>
              <div className="filter-group filter-action">
                <label>&nbsp;</label>
                <button className="search-btn" onClick={() => fetchData(filters, selectedDriver, searchTerm, 1)}><i className="bi bi-search me-2"></i>Search</button>
              </div>
              <div className="filter-group filter-action">
                <label>&nbsp;</label>
                <button className="reset-btn" onClick={resetFilters}><i className="bi bi-arrow-counterclockwise me-2"></i>Reset</button>
              </div>
            </div>
          </div>

          {loading ? <LoadingTable /> : data.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-inbox"></i>
              <h3>No Pending Rides</h3>
              <p>All caught up! No active rides found.</p>
            </div>
          ) : (
            <div className="orders-list">
              {data.map((row, idx) => {
                const statusCfg = getStatusConfig(row.status);
                const bookingType = getBookingType(row.items);
                const bColor = bookingColors[bookingType] || bookingColors['N/A'];
                const isTerminal = row.status === 0 || row.status === 7;

                return (
                  <div className="order-card" key={row._id} style={{ animationDelay: `${idx * 0.04}s` }}>
                    <div className="order-card-header">
                      <div className="header-left">
                        <span className="order-id">#{row.orderId}</span>
                        <span className="status-badge" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                          <i className={`bi ${statusCfg.icon} me-1`}></i>{statusCfg.label}
                        </span>
                        <span className="booking-badge" style={{ color: bColor.color, background: bColor.bg }}>
                          <i className="bi bi-tag me-1"></i>{bookingType}
                        </span>
                        <span className="sub-badge">{row?.items?.[0]?.title || 'N/A'}</span>
                      </div>
                      <div className="header-right">
                        {row.startTime && <span className="time-chip"><i className="bi bi-play-fill me-1"></i>Started</span>}
                        {row.startTime && row.endTime && <span className="time-chip done-chip"><i className="bi bi-check-all me-1"></i>Job Done</span>}
                      </div>
                    </div>

                    <div className="order-card-body">
                      {/* Route */}
                      <div className="info-col">
                        <div className="col-label"><i className="bi bi-map me-1"></i>Route</div>
                        <div className="route-wrap">
                          <div className="route-point">
                            <div className="route-dot" style={{ background: '#10b981', boxShadow: '0 0 0 3px #d1fae5' }}></div>
                            <div className="route-text">
                              <span className="route-tiny">PICKUP</span>
                              <span className="route-addr">{row?.items?.[0]?.pickup || row?.location || 'N/A'}</span>
                              <span className="route-extra">Full: {row?.pickupFullAddress || row?.items?.[0]?.pickupFullAddress || row?.items?.[0]?.pickup || row?.location || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="route-line"></div>
                          <div className="route-point">
                            <div className="route-dot" style={{ background: '#ef4444', boxShadow: '0 0 0 3px #fee2e2' }}></div>
                            <div className="route-text">
                              <span className="route-tiny">DROP</span>
                              <span className="route-addr">{row?.items?.[0]?.dropoff || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Customer */}
                      <div className="info-col">
                        <div className="col-label"><i className="bi bi-person me-1"></i>Customer</div>
                        <div className="detail-list">
                          <div className="detail-item">
                            <i className="bi bi-person-fill detail-icon" style={{ color: '#3b82f6' }}></i>
                            <div>
                              <div className="detail-main">{row?.details?.[0]?.username || 'N/A'}</div>
                              <div className="detail-sub">{row?.details?.[0]?.email || '—'}</div>
                            </div>
                          </div>
                          <div className="detail-item">
                            <i className="bi bi-telephone-fill detail-icon" style={{ color: '#10b981' }}></i>
                            <div className="detail-main">{row?.details?.[0]?.phone || 'N/A'}</div>
                          </div>
                          <div className="detail-item">
                            <i className="bi bi-telephone-plus-fill detail-icon" style={{ color: '#f59e0b' }}></i>
                            <div>
                              <div className="detail-main">{row?.alternativePhone || row?.items?.[0]?.alternativePhone || 'N/A'}</div>
                              <div className="detail-sub">Alternative phone</div>
                            </div>
                          </div>
                          <div className="detail-item">
                            <i className="bi bi-clock-fill detail-icon" style={{ color: '#8b5cf6' }}></i>
                            <div>
                              <div className="detail-main">{row?.items?.[0]?.date || 'N/A'}</div>
                              <div className="detail-sub">{row?.items?.[0]?.time || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Driver */}
                      <div className="info-col">
                        <div className="col-label"><i className="bi bi-person-badge me-1"></i>Driver</div>
                        {row.agentId ? (
                          <div className="driver-card assigned">
                            <DriverAvatar driver={row.agentId} />
                            <div className="driver-info">
                              <div className="driver-name">{row.agentId?.username}</div>
                              <div className="driver-phone">{row.agentId?.phone}</div>
                            </div>
                            <button className="change-btn" data-bs-toggle="modal" data-bs-target="#modal-driver"
                              onClick={() => selectDriver(row._id, row.agentId?._id, row?.category, 1, {
                                longitude: row?.items?.[0]?.pickupMeta?.lng ?? row?.longitude,
                                latitude: row?.items?.[0]?.pickupMeta?.lat ?? row?.latitude,
                              }, 2)} title="Change">
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                          </div>
                        ) : (
                          <div className="driver-card unassigned">
                            <div className="driver-empty"><i className="bi bi-person-x"></i></div>
                            <div className="driver-info"><div className="driver-name" style={{ color: '#94a3b8' }}>No Driver Assigned</div></div>
                            <button className="assign-btn" data-bs-toggle="modal" data-bs-target="#modal-driver"
                              onClick={() => selectDriver(row._id, '', row?.category, 1, {
                                longitude: row?.items?.[0]?.pickupMeta?.lng ?? row?.longitude,
                                latitude: row?.items?.[0]?.pickupMeta?.lat ?? row?.latitude,
                              }, 2)}>
                              <i className="bi bi-plus me-1"></i>Assign
                            </button>
                          </div>
                        )}
                        <BookingDetailsButton order={row} onClick={setSelectedBookingOrder} />
                      </div>

                      {/* Controls */}
                      <div className="info-col controls-col">
                        <div className="col-label"><i className="bi bi-gear me-1"></i>Controls</div>
                        <div className="ctrl-group">
                          <span className="ctrl-mini-label">Ride Status</span>
                          {!isTerminal ? (
                            <select className="status-select" value={row.status} onChange={handleLeadStatusChange(row._id)}>
                              <option value="1">🕐 Placed</option>
                              <option value="2">✅ Accepted</option>
                              <option value="5">🚀 Started</option>
                            </select>
                          ) : (
                            <span className="status-final" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                              <i className={`bi ${statusCfg.icon} me-1`}></i>{statusCfg.label}
                            </span>
                          )}
                        </div>
                        <div className="action-btns">
                          <Link className="act-btn view-btn" to={`/order/${row._id}`}><i className="bi bi-eye me-1"></i>View</Link>
                          {!isTerminal && !row.startTime && (
                            cancelOrderId === row._id ? (
                              <div className="cancel-form">
                                <select className="cancel-by-select" value={cancelledBy} onChange={(e) => setCancelledBy(e.target.value)}>
                                  <option value="admin">Cancel by admin</option>
                                  <option value="user">Cancel by user</option>
                                  <option value="driver">Cancel by driver</option>
                                </select>
                                <textarea rows={2} placeholder="Reason for cancellation..." value={cancelComment} onChange={(e) => setCancelComment(e.target.value)} />
                                <div className="cancel-form-row">
                                  <button className="act-btn confirm-btn" onClick={() => handleCancelOrder(row._id)}><i className="bi bi-check me-1"></i>Confirm</button>
                                  <button className="act-btn discard-btn" onClick={() => { setCancelOrderId(null); setCancelComment(''); setCancelledBy('admin'); }}><i className="bi bi-x"></i></button>
                                </div>
                              </div>
                            ) : (
                              <button className="act-btn cancel-btn" onClick={() => { setCancelledBy('admin'); setCancelOrderId(row._id); }}><i className="bi bi-x-circle me-1"></i>Cancel</button>
                            )
                          )}
                          {!isTerminal && (
                            <button className="act-btn complete-btn" onClick={() => handleCompleteOrder(row._id)}><i className="bi bi-check2-all me-1"></i>Complete</button>
                          )}
                          <button className="act-btn delete-btn" onClick={() => handleDelete(row._id)}><i className="bi bi-trash3 me-1"></i>Delete</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && data.length > 0 && (
            <div className="pagination-bar">
              <span className="page-info">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
              <div className="page-buttons">
                <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><i className="bi bi-chevron-left"></i> Prev</button>
                <span className="page-current">{currentPage}</span>
                <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next <i className="bi bi-chevron-right"></i></button>
              </div>
            </div>
          )}
        </div>

        {/* Driver Modal */}
        <div className="modal fade" id="modal-driver" tabIndex={-1} aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0" style={{ background: 'linear-gradient(135deg,#1d4ed8,#3b82f6)', color: '#fff' }}>
                <h5 className="modal-title fw-bold"><i className="bi bi-person-plus-fill me-2"></i>Assign Driver</h5>
                <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body p-4">
                <label className="form-label fw-semibold mb-2">Select Driver</label>
                <AssignDriverSelect
                  drivers={AllDriver}
                  selectedDriverId={DriverId}
                  onChange={handleDriverChange}
                />
                <select className="form-select d-none" value={DriverId || ''} onChange={(event) => setDriverId(event.target.value)}>
                  <option value="">— Select Driver —</option>
                  {AllDriver.length > 0 ? AllDriver.map(d => (
                    <option value={d._id} key={d._id} style={{ color: d.onDuty ? 'blue' : Number(d.online) === 1 ? '#059669' : '#dc2626' }}>
                      {d.username} {d.onDuty ? '(On Duty) ' : ''}{Number(d.online) === 1 ? '(Online)' : '(Offline)'}{d.distanceKm != null ? ` · ${Number(d.distanceKm).toFixed(2)} km` : ''}
                    </option>
                  )) : <option value="">No drivers available</option>}
                </select>
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-primary px-4" type="button" data-bs-dismiss="modal" onClick={handleClickDriverChange}><i className="bi bi-check-circle me-1"></i>Update Driver</button>
                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>
        <BookingDetailsModal order={selectedBookingOrder} hideOtp />
      </main>
      <style jsx>{`${sharedStyles('#f59e0b', '#fffbeb')}`}</style>
    </>
  );
}

/* ── Shared CSS factory ── */
function sharedStyles(accentColor, accentBg) {
  return `
    .orders-page { padding: 24px 28px; min-height: 100vh; background: #f1f5f9; font-family: 'Segoe UI', system-ui, sans-serif; }
    .page-header { margin-bottom: 24px; }
    .page-title { display: flex; align-items: center; gap: 14px; }
    .title-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; font-size: 22px; flex-shrink: 0; background: linear-gradient(135deg,#1d4ed8,#3b82f6); }
    .pending-icon { background: linear-gradient(135deg,#d97706,#f59e0b) !important; }
    .page-title h1 { margin: 0; font-size: 26px; font-weight: 700; color: #0f172a; line-height: 1.2; }
    .page-title p  { margin: 0; color: #64748b; font-size: 14px; }
    .filter-card { background: #fff; border-radius: 16px; padding: 20px 24px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,.08); }
    .filter-row { display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
    .filter-group { display: flex; flex-direction: column; gap: 6px; }
    .filter-group.flex-1 { flex: 1; }
    .filter-group label { font-size: 12px; font-weight: 600; color: #475569; text-transform: uppercase; letter-spacing: .04em; }
    .filter-group select { height: 40px; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 0 12px; font-size: 14px; color: #1e293b; background: #f8fafc; outline: none; min-width: 100px; }
    .search-input-wrap { position: relative; }
    .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 14px; }
    .search-input-wrap input { width: 100%; height: 40px; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 0 12px 0 36px; font-size: 14px; color: #1e293b; background: #f8fafc; outline: none; }
    .search-input-wrap input:focus { border-color: #3b82f6; background: #fff; }
    .search-btn { height: 40px; padding: 0 24px; background: linear-gradient(135deg,#1d4ed8,#3b82f6); color: #fff; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; white-space: nowrap; }
    .search-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(59,130,246,.4); }
    .orders-list { display: flex; flex-direction: column; gap: 14px; }
    .order-card { background: #fff; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,.07), 0 4px 16px rgba(0,0,0,.04); overflow: hidden; animation: slideUp .3s ease both; transition: box-shadow .2s, transform .2s; }
    .order-card:hover { box-shadow: 0 4px 20px rgba(59,130,246,.13); transform: translateY(-1px); }
    @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    .order-card-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 18px; background: #f8fafc; border-bottom: 1px solid #f1f5f9; flex-wrap: wrap; gap: 8px; }
    .header-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .header-right { display: flex; align-items: center; gap: 8px; }
    .order-id { font-size: 15px; font-weight: 700; color: #1d4ed8; }
    .status-badge, .booking-badge, .sub-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
    .sub-badge { background: #f1f5f9; color: #475569; }
    .time-chip { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
    .done-chip { background: #ecfdf5; color: #059669; border-color: #bbf7d0; }
    .order-card-body { display: grid; grid-template-columns: 2fr 1.5fr 1.5fr 1.3fr; }
    .info-col { padding: 16px 18px; border-right: 1px solid #f1f5f9; }
    .info-col:last-child { border-right: none; }
    .col-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #94a3b8; margin-bottom: 10px; }
    .route-wrap { display: flex; flex-direction: column; }
    .route-point { display: flex; align-items: flex-start; gap: 10px; }
    .route-dot { width: 10px; height: 10px; border-radius: 50%; margin-top: 4px; flex-shrink: 0; }
    .route-line { width: 2px; height: 16px; background: linear-gradient(#10b981,#ef4444); margin-left: 4px; opacity: .35; }
    .route-text { display: flex; flex-direction: column; gap: 1px; }
    .route-tiny { font-size: 9px; font-weight: 700; letter-spacing: .08em; color: #94a3b8; }
    .route-addr { font-size: 13px; color: #1e293b; font-weight: 500; line-height: 1.35; }
    .route-extra { font-size: 11px; color: #64748b; line-height: 1.35; }
    .detail-list { display: flex; flex-direction: column; gap: 8px; }
    .detail-item { display: flex; align-items: flex-start; gap: 8px; }
    .detail-icon { font-size: 13px; margin-top: 2px; flex-shrink: 0; }
    .detail-main { font-size: 13px; font-weight: 600; color: #1e293b; line-height: 1.3; }
    .detail-sub  { font-size: 11px; color: #64748b; }
    .driver-card { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 10px; }
    .driver-card.assigned   { background: #f0fdf4; border: 1px solid #bbf7d0; }
    .driver-card.unassigned { background: #fef2f2; border: 1px solid #fecaca; }
    .driver-avatar { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg,#1d4ed8,#3b82f6); color: white; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .driver-avatar-img { border-radius: 50%; object-fit: cover; flex-shrink: 0; border: 1px solid #dbeafe; }
    .driver-select-option { display: flex; align-items: center; gap: 10px; }
    .driver-select-option.selected { gap: 8px; }
    .driver-select-copy { min-width: 0; }
    .driver-select-name { font-size: 13px; font-weight: 700; color: #1e293b; line-height: 1.25; }
    .driver-select-meta { font-size: 11px; color: #64748b; line-height: 1.25; }
    .driver-status-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 2px 7px; margin-left: 6px; font-size: 10px; font-weight: 800; line-height: 1.2; }
    .driver-empty { width: 34px; height: 34px; border-radius: 50%; background: #fecaca; color: #ef4444; font-size: 16px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .driver-info { flex: 1; min-width: 0; }
    .driver-name  { font-size: 13px; font-weight: 600; color: #1e293b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .driver-phone { font-size: 11px; color: #64748b; }
    .change-btn { width: 28px; height: 28px; border-radius: 8px; border: none; background: #dbeafe; color: #1d4ed8; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .15s; }
    .change-btn:hover { background: #1d4ed8; color: #fff; }
    .assign-btn { padding: 4px 10px; border-radius: 8px; border: none; background: #ef4444; color: white; font-size: 12px; font-weight: 600; cursor: pointer; flex-shrink: 0; transition: all .15s; }
    .assign-btn:hover { background: #dc2626; }
    .controls-col { display: flex; flex-direction: column; gap: 10px; }
    .ctrl-group   { display: flex; flex-direction: column; gap: 4px; }
    .ctrl-mini-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .06em; }
    .status-select { width: 100%; height: 34px; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 0 8px; font-size: 13px; font-weight: 600; color: #1e293b; background: #f8fafc; outline: none; }
    .status-final { display: inline-flex; align-items: center; padding: 5px 10px; border-radius: 8px; font-size: 12px; font-weight: 600; }
    .action-btns { display: flex; flex-direction: column; gap: 6px; }
    .act-btn { display: inline-flex; align-items: center; justify-content: center; padding: 7px 10px; border-radius: 8px; font-size: 12px; font-weight: 600; border: none; cursor: pointer; text-decoration: none; transition: all .15s; width: 100%; }
    .view-btn      { background: #eff6ff; color: #1d4ed8; }
    .view-btn:hover { background: #1d4ed8; color: #fff; }
    .cancel-btn    { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
    .cancel-btn:hover { background: #f59e0b; color: #fff; }
    .complete-btn  { background: #ecfdf5; color: #059669; border: 1px solid #bbf7d0; }
    .complete-btn:hover { background: #059669; color: #fff; }
    .delete-btn    { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
    .delete-btn:hover { background: #dc2626; color: #fff; }
    .confirm-btn   { background: #ef4444; color: #fff; flex: 1; }
    .confirm-btn:hover { background: #dc2626; }
    .discard-btn   { background: #f1f5f9; color: #64748b; width: 36px; padding: 7px; flex-shrink: 0; }
    .cancel-form textarea { width: 100%; border: 1.5px solid #fde68a; border-radius: 8px; padding: 6px 10px; font-size: 12px; resize: none; outline: none; color: #1e293b; background: #fffbeb; margin-bottom: 6px; }
    .cancel-by-select { width: 100%; height: 34px; border: 1.5px solid #fde68a; border-radius: 8px; padding: 0 10px; font-size: 12px; color: #1e293b; background: #fff; outline: none; margin-bottom: 6px; }
    .cancel-form-row { display: flex; gap: 6px; }
    .empty-state { text-align: center; padding: 80px 20px; background: #fff; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
    .empty-state i { font-size: 56px; color: #cbd5e1; display: block; margin-bottom: 16px; }
    .empty-state h3 { color: #475569; font-weight: 600; margin-bottom: 6px; }
    .empty-state p  { color: #94a3b8; font-size: 14px; }
    .pagination-bar { display: flex; align-items: center; justify-content: space-between; margin-top: 20px; padding: 14px 20px; background: #fff; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
    .page-info { font-size: 14px; color: #64748b; }
    .page-info strong { color: #1e293b; }
    .page-buttons { display: flex; align-items: center; gap: 8px; }
    .page-btn { display: flex; align-items: center; gap: 4px; padding: 7px 16px; border: 1.5px solid #e2e8f0; border-radius: 8px; background: #fff; color: #475569; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; }
    .page-btn:hover:not(:disabled) { border-color: #3b82f6; color: #1d4ed8; background: #eff6ff; }
    .page-btn:disabled { opacity: .4; cursor: not-allowed; }
    .page-current { width: 36px; height: 36px; background: #1d4ed8; color: #fff; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; }
    @media (max-width: 1100px) { .order-card-body { grid-template-columns: 1fr 1fr; } .info-col { border-right: none; border-bottom: 1px solid #f1f5f9; } .info-col:nth-child(odd) { border-right: 1px solid #f1f5f9; } .info-col:nth-last-child(-n+2) { border-bottom: none; } }
    @media (max-width: 640px) { .order-card-body { grid-template-columns: 1fr; } .info-col { border-right: none !important; border-bottom: 1px solid #f1f5f9; } .orders-page { padding: 16px; } }
  `;
}

export default PendingOrder;
